import { SliderProps } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconTooltipProps } from '../tooltip';

const useStyles = makeStyles({ name: 'SliderWrapper' })(theme => ({
	root: {
		display: 'flex',
		flexDirection: 'column'
	},
	label: {
		marginBottom: 16,
		fontSize: '16px',
		lineHeight: '24px'
	},
	value: {
		marginBottom: 20,
		fontSize: '20px',
		lineHeight: '24px',
		fontWeight: 'bold'
	},
	panelMinMax: {
		display: 'flex',
		justifyContent: 'space-between',
		fontSize: '12px',
		lineHeight: '16px',
		marginTop: 12
	}
}));

export type SliderWrapperProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
	children: React.ReactElement<SliderProps>;
	label: string;
	labelMin?: string;
	labelMax?: string;
	tooltip?: React.ReactElement<IconTooltipProps>;
	formatValue?: (value: number) => string;
	classes?: Partial<ReturnType<typeof useStyles>['classes']>;
};

export const SliderWrapper: React.FC<SliderWrapperProps> = (props: SliderWrapperProps) => {
	const { children, label, tooltip, labelMin, labelMax, formatValue, className, classes, ...other } = props;
	const { value, min, max } = props.children.props;
	const { classes: styles, cx } = useStyles(undefined, { props: { classes } });

	const valueText = formatValue?.(value) ?? value;
	const minValueText = labelMin ?? formatValue?.(min) ?? min;
	const maxValueText = labelMax ?? formatValue?.(max) ?? max;

	return (
		<div data-test='slider-wrapper' className={cx(styles.root, className)} {...other}>
			<div data-test='slider-wrapper-label' className={styles.label}>
				{label}
				{tooltip &&
					<React.Fragment>
						<span>{'\u00A0'}</span>
						{tooltip}
					</React.Fragment>}
			</div>
			<div data-test='slider-wrapper-value' className={styles.value}>
				{valueText}
			</div>
			{children}
			<div className={styles.panelMinMax}>
				<span data-test='slider-wrapper-value-min'>{minValueText}</span>
				<span data-test='slider-wrapper-value-max'>{maxValueText}</span>
			</div>
		</div>
	);
};
